import * as React from 'react';
import * as cx from 'classnames';
import { Sticky, StickyChildArgs } from 'react-sticky';
import {
  withStyles,
  WithStyles,
  createStyles,
  Theme,
} from '@material-ui/core/styles';

import MediaQuery from 'ha/helpers/MediaQuery';
import units from 'ha/theme/units';

import DateFilterButton from '../containers/SearchFilters/DateFilterButton';

import SearchLocationTitle from '../containers/SearchLocationTitle';
import SearchAdvertiserTitle from '../containers/SearchAdvertiserTitle';

import DateFilter from '../containers/SearchFilters/DateFilter';
import OtherFilterDesktop from '../containers/SearchFilters/OtherFilterDesktop';
import OtherFilterMobile from '../containers/SearchFilters/OtherFilterMobile';
import PriceFilterDesktop from '../containers/SearchFilters/PriceFilterDesktop';
import SearchAlertDesktop from '../containers/SearchAlerts/SearchAlertDesktop';

import SearchAlertMobile from '../containers/SearchAlerts/SearchAlertMobileButton';
import ToggleMobileMap from '../containers/ToggleMobileMap';
import PriceFilterButton from '../containers/SearchFilters/PriceFilterButton';
import OtherFilterButton from '../containers/SearchFilters/OtherFilterButton';
import { DeviceType } from 'ha/components/Filters/DateFilter/DateFilter';
import { WidthType } from 'ha/components/Filters/FilterPopover';
import { filterUpdateButtonText, DefaultTextType } from '../utils';
import { withIntl, IntlProps } from 'ha/i18n';

interface OwnProps {
  isAuthenticated: boolean;
  previewNumberOfListings?: number;
  isAdvertiserSearch: boolean;
  isSearchEmpty: boolean;
}

const styles = (t: Theme) =>
  createStyles({
    searchLocationTitle: {
      margin: units.trbl(0, 1.5, 0.5, 1.5),
    },
    searchLocationTitleMobile: {
      margin: units.trbl(0, 0.7, 1, 0.7),
    },
    buttonContainer: {
      flexShrink: 0,
      marginRight: units.rem(0.5),
    },
    mobileHorizontalPadding: {
      marginRight: units.rem(0.7),
      marginLeft: units.rem(0.7),
    },
    alertContainer: {
      marginTop: units.rem(0.5),
    },
    flexStill: {
      display: 'flex',
      flexGrow: 0,
      flexShrink: 0,
    },
    buttonFilterContainer: {
      display: 'flex',
      overflowX: 'auto',
      flexGrow: 1,
    },
    flex: {
      display: 'flex',
    },
    pr2: {
      paddingRight: units.rem(2),
    },
    pl07: {
      paddingLeft: units.rem(0.7),
    },
    mb05: {
      marginBottom: units.rem(0.5),
    },
    stickyContent: {
      marginBottom: units.rem(1),
      position: 'relative',
    },
    controlsContainer: {
      marginTop: units.rem(1),
      marginRight: units.rem(1.5),
      marginLeft: units.rem(1.5),
    },
    controlsContainerMobile: {
      marginTop: units.rem(1),
      marginRight: units.rem(0.7),
      marginLeft: units.rem(0.7),
    },
    controlsContainerMobileSticky: {
      zIndex: 2,
      paddingTop: units.rem(1),
      boxShadow: '0 3px 9px rgba(0, 0, 0, 0.5)',
      background: 'white',
    },
  });

type Props = OwnProps & IntlProps & WithStyles<typeof styles>;

export const SearchOverview: React.SFC<Props> = ({
  classes,
  isAuthenticated,
  previewNumberOfListings,
  intl: { T },
  isAdvertiserSearch,
  isSearchEmpty,
}) => (
  <MediaQuery>
    {({ lg, md }) => (
      <React.Fragment>
        <Sticky>
          {({ style, isSticky }: StickyChildArgs) => (
            <div
              style={!lg ? style : {}}
              className={cx({
                [classes.controlsContainer]: lg && !isSticky,
                [classes.controlsContainerMobile]: !lg && !isSticky,
                [classes.controlsContainerMobileSticky]: !lg && isSticky,
              })}
            >
              <div
                className={cx(classes.stickyContent, {
                  [classes.mb05]: !lg,
                  [classes.pr2]: !lg && isSticky && !isAuthenticated,
                  [classes.pl07]: !lg && isSticky,
                })}
              >
                <div className={classes.flex}>
                  <div className={classes.buttonFilterContainer}>
                    <div className={classes.buttonContainer}>
                      <DateFilter
                        button={
                          <DateFilterButton size={lg ? 'medium' : 'small'} />
                        }
                        type={lg ? DeviceType.Desktop : DeviceType.Mobile}
                        updateButtonText={filterUpdateButtonText(
                          T,
                          DefaultTextType.Update,
                          previewNumberOfListings,
                        )}
                      />
                    </div>
                    <div className={classes.buttonContainer}>
                      {lg ? (
                        <OtherFilterDesktop
                          button={<OtherFilterButton size="medium" />}
                          updateButtonText={filterUpdateButtonText(
                            T,
                            DefaultTextType.Update,
                            previewNumberOfListings,
                          )}
                        />
                      ) : (
                        <OtherFilterMobile size="small" />
                      )}
                    </div>
                    {!lg && (
                      <div className={classes.buttonContainer}>
                        <ToggleMobileMap />
                      </div>
                    )}
                    <div className={classes.buttonContainer}>
                      {lg && (
                        <PriceFilterDesktop
                          width={WidthType.lg}
                          priceFilterTitle={T('Budget')}
                          button={<PriceFilterButton />}
                          updateButtonText={filterUpdateButtonText(
                            T,
                            DefaultTextType.Update,
                            previewNumberOfListings,
                          )}
                        />
                      )}
                    </div>
                  </div>

                  {!isAdvertiserSearch && (
                    <div className={classes.flexStill}>
                      {lg && !isSearchEmpty && <SearchAlertDesktop />}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </Sticky>
        <div
          className={cx({
            [classes.searchLocationTitle]: lg,
            [classes.searchLocationTitleMobile]: !lg,
          })}
        >
          {isAdvertiserSearch ? (
            <SearchAdvertiserTitle />
          ) : (
            <SearchLocationTitle />
          )}
          {!lg && !isAdvertiserSearch && !isSearchEmpty && (
            <div className={classes.alertContainer}>
              <SearchAlertMobile />
            </div>
          )}
        </div>
      </React.Fragment>
    )}
  </MediaQuery>
);

export default withIntl(withStyles(styles)(SearchOverview));
